import { useEffect, useRef, type ReactNode } from "react";
import { YStack, Text, Spinner } from "tamagui";

import { colors } from "@styles/colors";

import type { OrderPaymentStatus } from "../Account/API/handlePaymentAPI";

/**
 * 「付款確認中」：扣款已送出、銀行還沒回結果（app-api 回 202，或開頁時 payment_in_progress）。
 *
 * 這時候絕對不能讓客戶再按一次付款——前一筆可能已經扣款成功，只是我們還沒收到。
 * 後端會向 TapPay 查單收尾（對帳排程每 5 分鐘），這頁每 5 秒重查一次，結果出來自動切畫面。
 */

const POLL_MS = 5_000;

/**
 * enabled 期間每 5 秒查一次付款狀態，結果確定（已付款，或不再處理中）就呼叫 onSettled 一次。
 * fetchStatus 查詢失敗回 null，下一輪再試。
 */
export function usePaymentResultPoll(
  enabled: boolean,
  fetchStatus: () => Promise<OrderPaymentStatus | null>,
  onSettled: (status: OrderPaymentStatus) => void,
) {
  // 每次 render 傳進來的都是新函式；用 ref 拿最新的，避免輪詢因為重新 render 而重來
  const fetchRef = useRef(fetchStatus);
  const settledRef = useRef(onSettled);
  fetchRef.current = fetchStatus;
  settledRef.current = onSettled;

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const tick = async () => {
      const st = await fetchRef.current();
      if (!alive || !st) return;
      if (st.paid || !st.payment_in_progress) settledRef.current(st);
    };
    const id = setInterval(tick, POLL_MS);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [enabled]);
}

export const PaymentProcessingView = ({ header }: { header: ReactNode }) => (
  <YStack height="100dvh" width="100vw" background={colors.bg}>
    {header}
    <YStack f={1} ai="center" jc="center" gap="$2" px="$6">
      <Spinner size="large" color={colors.primary} />
      <Text fontSize={15} fontWeight="700" color={colors.text} mt="$2">
        付款確認中
      </Text>
      <Text fontSize={12} color={colors.hint} ta="center">
        銀行回應較慢，正在確認這筆付款的結果。請不要重新付款，結果出來後這頁會自動更新。
      </Text>
    </YStack>
  </YStack>
);
